import React, { useContext, useEffect, useState } from 'react';
import { Button, ColumnProps, FormSelect, FormTextArea, Row, Table } from '@digital/ui';
import { Tooltip } from 'antd';
import QuestionarioContextProvider from '../../../../../hooks/useQuestionario';
import CurrencyInput from '@app/components/CurrencyInput';
import { ETipoReavaliacaoDemanda } from '@app/enums/ETipoReavaliacaoDemanda';

const CRITERIO_AJUSTE_VALOR_OPTIONS = [
    { text: 'Melhor proposta negociada', value: 1 },
    { text: 'Melhor proposta equalizada', value: 2 },
    { text: 'Baseline ACS', value: 3 },
];
import { downloadArquivo, getDocumentosOrdemCompraEditavelRequisitante, incluirDocumentoRequisitante, substituirAnexoRequisitante } from '@app/api/sas/sasDocumentoOrdemCompra';
import { Space, Visibility } from '@app/components';
import { UploadModalHistorico } from '@app/components/Upload/uploadModalHistorico';

interface Props {
    disabled?: boolean;
}
const DefinicaoReavaliacao = ({ disabled = false } : Props) => {
    const { demandaData, ordemCompraId, tipoReavaliacao, setTipoReavaliacao } = useContext(QuestionarioContextProvider);
    const [documentos, setDocumentos] = useState([]);
    const [exibirDocumentos, setExibirDocumentos] = useState(false);
    const [reloadDocumentos, setReloadDocumentos] = useState(false);
    const [alterarDocumentacao, setAlterarDocumentacao] = useState(!!demandaData && !!demandaData.alterarDocumentacao);

    useEffect(() => {
        const exibir = tipoReavaliacao === ETipoReavaliacaoDemanda.ReducaoEscopo || alterarDocumentacao;
        setExibirDocumentos(exibir);
    }, [tipoReavaliacao, alterarDocumentacao]);

    useEffect( () => {
        if (exibirDocumentos && ordemCompraId) {
            getDocumentosOrdemCompraEditavelRequisitante(ordemCompraId)
                .then((data: any) => {
                    if (data) {
                        setDocumentos(data);
                    }
                });
        }
    }, [exibirDocumentos, reloadDocumentos]);

    const columnsDocumentos = () => {
        let columns: ColumnProps<any>[] = []

        columns.push({
            title: 'Descrição',
            dataIndex: 'descricao',
        });

        columns.push({
            title: 'Arquivo',
            dataIndex: 'arquivoNomeDownload',
        });

        columns.push({
            title: 'Ações',
            width: '5%',
            render(text, record, index) {
                return <>
                    <UploadModalHistorico
                        tooltip={!record.arquivoId || record.multiple ? "Adicionar Arquivo" : "Substituir Anexo"}
                        title={!record.arquivoId || record.multiple ? "Adicionar Arquivo" : "Substituir Anexo"}
                        buttonIcon='ui-upload'
                        hasJustification={true}
                        hasDatePicker={false}
                        hasUpload={true}
                        showUploadList={true}
                        onSubmit={async (values) => 
                            {
                                return await substituirAnexoRequisitante({
                                    justificativa: values.justificativa || "",
                                    arquivo: values.arquivo,
                                    sasDocumentoOrdemCompraId: record.id
                                }).then((data: any) => {
                                    if (data && data.valid) {
                                        setReloadDocumentos(!reloadDocumentos);
                                        return true;
                                    }
                                    return false;
                                });
                            }
                        }
                        visible={disabled === false}
                    />

                    <Tooltip title="Download Arquivo">
                        <Button
                            icon="ui-download"
                            type="link"
                            onClick={() => {
                                downloadArquivo(record.arquivoId, record.arquivoNomeDownload);
                            }}
                        />
                    </Tooltip>
                </>;
            },
        });

        return columns;
    }
    
    return (
        <>
            <Row>
                <FormSelect
                    label="Tipo de Reavaliação"
                    span={3}
                    name="tipoReavaliacao"
                    id='mrn-select-tipo-reavaliacao'
                    value={tipoReavaliacao}
                    disabled={disabled}
                    onSelect={e => setTipoReavaliacao(Number(e))}
                    options={[
                        { text: 'Redução de Escopo', value: ETipoReavaliacaoDemanda.ReducaoEscopo },
                        { text: 'Abrir Nova RC', value: ETipoReavaliacaoDemanda.AbrirNovaRC },
                        // { text: 'Cancelar a Demanda', value: ETipoReavaliacaoDemanda.Cancelar },
                    ]}
                />
                <CurrencyInput label="Valor Menor Proposta" name="valorMenorProposta" span={3} disabled />
                <Visibility visible={tipoReavaliacao === ETipoReavaliacaoDemanda.AbrirNovaRC}>
                    <CurrencyInput label="Valor da Contratação" name="valorContratacaoNovaRC" span={3} disabled={disabled} />
                    <FormSelect
                        label="Haverá Alteração da Documentação?"
                        span={3}
                        name="alterarDocumentacao"
                        id='mrn-select-alteracao-documentacao'
                        disabled={disabled}
                        onSelect={e => setAlterarDocumentacao(!!e)}
                        options={[
                            { text: 'Sim', value: true },
                            { text: 'Não', value: false },
                        ]}
                    />
                </Visibility>
            </Row>

            <Row>
                <FormSelect
                    label="Critério para ajuste de valor"
                    name="criterioAjusteValor"
                    span={3}
                    disabled
                    options={CRITERIO_AJUSTE_VALOR_OPTIONS}
                />
            </Row>

            <Row>
                <div style={{ width: '100%', padding: '0 4px' }}>
                    <label style={{ display: 'block', marginBottom: 4, color: 'rgba(0,0,0,0.85)', fontSize: 14 }}>
                        Justificativa solicitação de ajuste orçamentário
                    </label>
                    <div
                        className="ant-input ant-input-disabled"
                        style={{ minHeight: 100, height: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', cursor: 'default', lineHeight: '1.5715', padding: '4px 11px', overflowY: 'auto' }}
                    >
                        {demandaData && demandaData.usuarioAjuste && (
                            <div style={{ color: '#006129', fontWeight: 600, marginBottom: 6 }}>
                                {demandaData.usuarioAjuste}
                                {demandaData.dataAjuste ? ' - ' + new Date(demandaData.dataAjuste).toLocaleString() : ''}
                            </div>
                        )}
                        <span>{demandaData && demandaData.justificativaAjusteOrcamentario}</span>
                    </div>
                </div>
            </Row>

            <Visibility visible={exibirDocumentos}>
                <UploadModalHistorico
                    tooltip="INCLUIR ANEXO"
                    title="INCLUIR ANEXO"
                    buttonUploadText='ADICIONAR ARQUIVO'
                    buttonModalText='INCLUIR ANEXO'
                    buttonIcon='ui-upload'
                    hasJustification={true}
                    labelJustification='Descrição do Documento'
                    placeholderJustification='Descrição do Documento'
                    hasDatePicker={false}
                    hasUpload={true}
                    showUploadList={true}
                    onSubmit={async (values) =>
                        await incluirDocumentoRequisitante({
                            ordemCompraId: ordemCompraId || '',
                            descricao: values.justificativa || '',
                            file: values.arquivo,
                        }).then((data: any) => {
                            if (data && data.valid) {
                                setReloadDocumentos(!reloadDocumentos);
                                return true;
                            }
                            return false;
                        })
                    }
                    buttonModalFooterText='Salvar'
                    visible={disabled === false}
                />
                <Space mb={16} />
                <Table
                    dataSource={documentos}
                    columns={columnsDocumentos()}
                    rowKey="id"
                    pagination={false}
                />
            </Visibility>
        </>
    );
}

export default DefinicaoReavaliacao;
